/**
 * List of domains that are whitelisted and will not be processed for cookie consent
 */
const WHITE_LISTED_DOMAINS = [
  "norkon.net",
  "ceros.com",
  "amuselabs.com",
  "link.theverge.com",
  "link.eater.com",
  "link.sbnation.com",
  "link.vox.com",
];

/**
 * Check if HTML contains any whitelisted domains
 * @param html - The HTML string to check
 * @param whitelistedDomains - Array of whitelisted domains
 * @returns True if HTML contains any whitelisted domain
 */
function containsWhitelistedDomain(
  html: string,
  whitelistedDomains: string[] = WHITE_LISTED_DOMAINS,
): boolean {
  if (whitelistedDomains.length === 0) {
    return false;
  }

  return whitelistedDomains.some((domain) => {
    const normalizedDomain = domain.toLowerCase();
    return html.toLowerCase().includes(normalizedDomain);
  });
}

/**
 * Extract class attribute value from HTML attributes string, handling mixed quotes
 * @param attributes - The attributes string (e.g., ' class="my\'class\'" width="100"')
 * @returns Object with classValue, fullMatch, startIndex, endIndex or null if not found
 */
function extractClassAttribute(attributes: string): {
  classValue: string;
  fullMatch: string;
  startIndex: number;
  endIndex: number;
} | null {
  const classIndex = attributes.indexOf(" class=");
  if (classIndex === -1) {
    return null;
  }

  const startIndex = classIndex + 7; // After ' class='
  const firstChar = attributes[startIndex];

  if (firstChar !== '"' && firstChar !== "'") {
    return null;
  }

  // Find the matching closing quote, handling escaped quotes
  let endIndex = startIndex + 1;
  while (endIndex < attributes.length) {
    if (attributes[endIndex] === firstChar) {
      // Check if this quote is escaped by looking at the previous character
      if (endIndex > startIndex + 1 && attributes[endIndex - 1] === "\\") {
        // This is an escaped quote, continue searching
        endIndex++;
        continue;
      }
      // Found matching quote (not escaped)
      return {
        classValue: attributes.substring(startIndex + 1, endIndex),
        fullMatch: attributes.substring(classIndex + 1, endIndex + 1), // Include the quotes
        startIndex: classIndex + 1,
        endIndex: endIndex + 1,
      };
    }
    endIndex++;
  }

  return null; // No matching quote found
}

/**
 * Process attributes to add or update class attribute with OneTrust category
 * Handles multiple class attributes by combining all classes into one attribute
 * @param attributes - The attributes string
 * @param categoryClass - The OneTrust category class to add
 * @returns Object with combinedClass and finalAttributes
 */
function processClassAttribute(
  attributes: string,
  categoryClass: string,
): {
  combinedClass: string;
  finalAttributes: string;
} {
  let result = attributes;
  const allClassValues: string[] = [];

  // Find all class attributes and collect their values
  let classIndex = result.indexOf(" class=");
  while (classIndex !== -1) {
    const classInfo = extractClassAttribute(result.substring(classIndex));
    if (classInfo) {
      // Only add non-empty class values
      if (classInfo.classValue.trim()) {
        allClassValues.push(classInfo.classValue.trim());
      }

      // Remove this class attribute
      const beforeClass = result.substring(0, classIndex + 1);
      const afterClass = result.substring(
        classIndex + 1 + classInfo.fullMatch.length,
      );
      result = beforeClass + afterClass;

      // Find the next class attribute
      classIndex = result.indexOf(" class=", classIndex);
    } else {
      break;
    }
  }

  // Combine all class values with the category class
  const allClasses = [...allClassValues, categoryClass];
  const combinedClass = allClasses.join(" ");

  // Clean up extra spaces and ensure proper spacing
  const cleanedAttributes = result.replace(/\s+/g, " ").trim();
  const finalAttributes = cleanedAttributes ? ` ${cleanedAttributes}` : "";

  return { combinedClass, finalAttributes };
}

function processHtmlForCookieConsent(html: string, categoryId: string): string {
  if (!html || typeof html !== "string" || !categoryId) {
    return html;
  }

  // Skip all processing if HTML contains any whitelisted domains
  if (containsWhitelistedDomain(html)) {
    return html;
  }

  const categoryClass = `optanon-category-${categoryId}`;

  return (
    html
      // Block iframes: src -> data-src + add class
      .replace(/<iframe([^>]*)\ssrc=/gi, `<iframe$1 data-src=`)
      .replace(/<iframe([^>]*)>/gi, (_, attributes: string) => {
        const { combinedClass, finalAttributes } = processClassAttribute(
          attributes,
          categoryClass,
        );
        return `<iframe class="${combinedClass}"${finalAttributes}>`;
      })
      // Block scripts: add type="text/plain" to scripts without type attribute + add class
      .replace(/<script([^>]*)>/gi, (_, attributes: string) => {
        const { combinedClass, finalAttributes } = processClassAttribute(
          attributes,
          categoryClass,
        );

        // Check if script already has a type attribute
        const hasType = /\stype\s*=/i.test(attributes);

        if (hasType) {
          // Convert text/javascript to text/plain
          const processedAttributes = finalAttributes.replace(
            /\stype=["']?text\/javascript["']?/gi,
            ' type="text/plain"',
          );
          return `<script class="${combinedClass}"${processedAttributes}>`;
        } else {
          // Add type="text/plain" to scripts without type attribute
          return `<script class="${combinedClass}" type="text/plain"${finalAttributes}>`;
        }
      })
  );
}

export { processHtmlForCookieConsent };
